import { Maybe, nothing } from 'maybeasy';
import { Error } from '../../../../../../../../../../../ErrorHandling';
import { ParticipantResource } from '../../../../../../../../../../../ParticipantsStore/Types';
import { Resource } from '../../../../../../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../../../../../../Translations';
import { guessTimezone } from '../../../../../../../../../../../Utils/timezone';
import { GroupParticipantsResource } from '../../../../../../../../../../People/Group/GroupParticipantsStore/Types';

export type GroupOrParticipantResource = ParticipantResource | GroupParticipantsResource;

export interface ProductLicenseInformation {
  publicName: string;
  allocatedLicenses: number;
  activationsRemaining: Maybe<number>;
  alreadyActiveLicenses: number;
  newLicenseAllocations: number;
  potentialLicenseActivations: number;
  daysUntilRenewal: number;
}
export type ProductLicenseInformationResource = Resource<ProductLicenseInformation>;

export type Invitable = Inviting | Submitting;

export type SelectParticipantsState =
  | Ready
  | InvitingError
  | NewParticipant
  | NewGroup
  | RemoveParticipant
  | RemoveGroup
  | GetLicenseInformation;

interface Ready extends ProductLicenseInformation {
  kind: 'ready';
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface InvitingError extends Error, ProductLicenseInformation {
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface NewParticipant extends ProductLicenseInformation {
  kind: 'new-participant';
  participantId: string;
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface NewGroup extends ProductLicenseInformation {
  kind: 'new-group';
  groupId: string;
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface RemoveParticipant extends ProductLicenseInformation {
  kind: 'remove-participant';
  participantId: string;
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface RemoveGroup extends ProductLicenseInformation {
  kind: 'remove-group';
  groupId: string;
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export interface Submitting extends ProductLicenseInformation {
  kind: 'submitting';
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
  timeZone: string;
  skipCommunications: boolean;
}

export interface Inviting extends ProductLicenseInformation {
  kind: 'inviting';
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
  timeZone: string;
  skipCommunications: boolean;
}

export interface ParticipantsInvitedSuccessfully {
  sendEmailDate: Date;
  successfulCommunicationsCount: number;
  skipCommunications: boolean;
}

export interface ParticipantsInvited {
  kind: 'participants-invited';
  successfulCommunicationsCount: number;
  sendEmailDate: Date;
  skipCommunications: boolean;
}

export interface GetLicenseInformation extends ProductLicenseInformation {
  kind: 'get-license-information';
  newGroupsAndParticipants: ReadonlyArray<GroupOrParticipantResource>;
}

export type ParticipantsInvitedResource = Resource<ParticipantsInvitedSuccessfully>;

export const readyWithLicenseInformation = (
  state: GetLicenseInformation,
  productLicenseInformation: ProductLicenseInformationResource,
): Ready => ({
  ...state,
  kind: 'ready',
  publicName: productLicenseInformation.payload.publicName,
  allocatedLicenses: productLicenseInformation.payload.allocatedLicenses,
  activationsRemaining: productLicenseInformation.payload.activationsRemaining,
  alreadyActiveLicenses: productLicenseInformation.payload.alreadyActiveLicenses,
  newLicenseAllocations: productLicenseInformation.payload.newLicenseAllocations,
  potentialLicenseActivations: productLicenseInformation.payload.potentialLicenseActivations,
  daysUntilRenewal: productLicenseInformation.payload.daysUntilRenewal,
});

export const back = (state: Inviting): Ready => ({
  ...state,
  kind: 'ready',
});

export const participantsInvited = (
  participantsInvitedResource: ParticipantsInvitedResource,
): ParticipantsInvited => ({
  kind: 'participants-invited',
  successfulCommunicationsCount: participantsInvitedResource.payload.successfulCommunicationsCount,
  sendEmailDate: participantsInvitedResource.payload.sendEmailDate,
  skipCommunications: participantsInvitedResource.payload.skipCommunications,
});

export const addNewParticipant = (
  state: Ready | InvitingError,
  participantId: string,
): NewParticipant => {
  const { kind, ...previous } = state;
  return {
    kind: 'new-participant',
    ...previous,
    participantId,
  };
};

export const addNewGroup = (state: Ready | InvitingError, groupId: string): NewGroup => {
  const { kind, ...previous } = state;
  return {
    kind: 'new-group',
    ...previous,
    groupId,
  };
};

export const removeParticipant = (
  state: Ready | InvitingError,
  participantId: string,
): RemoveParticipant => {
  const { kind, ...previous } = state;
  return {
    kind: 'remove-participant',
    ...previous,
    participantId,
  };
};

export const removeGroup = (state: Ready | InvitingError, groupId: string): RemoveGroup => {
  const { kind, ...previous } = state;
  return {
    kind: 'remove-group',
    ...previous,
    groupId,
  };
};

export const inviting = (state: Ready | InvitingError): Inviting => ({
  ...state,
  kind: 'inviting',
  newGroupsAndParticipants: state.newGroupsAndParticipants,
  timeZone: guessTimezone(),
  skipCommunications: false,
});

export const submit = (state: Inviting): Submitting => ({
  ...state,
  kind: 'submitting',
});

export const getLicenseInformation = (): GetLicenseInformation => {
  return {
    kind: 'get-license-information',
    newGroupsAndParticipants: [],
    publicName: '',
    allocatedLicenses: 0,
    activationsRemaining: nothing(),
    alreadyActiveLicenses: 0,
    newLicenseAllocations: 0,
    potentialLicenseActivations: 0,
    daysUntilRenewal: 0,
  };
};

export const getLicenseInformationWithNewParticipant = (
  state: NewParticipant,
  participant: ParticipantResource,
): GetLicenseInformation => {
  const { kind, newGroupsAndParticipants, ...previous } = state;

  return {
    kind: 'get-license-information',
    ...previous,
    newGroupsAndParticipants: [participant, ...newGroupsAndParticipants],
  };
};

export const getLicenseInformationWithNewGroup = (
  state: NewGroup,
  group: GroupParticipantsResource,
): GetLicenseInformation => {
  const { kind, newGroupsAndParticipants, ...previous } = state;

  return {
    kind: 'get-license-information',
    ...previous,
    newGroupsAndParticipants: [group, ...newGroupsAndParticipants],
  };
};

export const licenseInformationWithRemovedParticipant = (
  state: RemoveParticipant,
): GetLicenseInformation => {
  const { kind, newGroupsAndParticipants, ...previous } = state;
  const groupsAndParticipants = newGroupsAndParticipants.filter(
    (participantOrGroup) =>
      (participantOrGroup.payload.id.toString() !== state.participantId &&
        'email' in participantOrGroup.payload) ||
      'participants' in participantOrGroup.payload,
  );

  return {
    kind: 'get-license-information',
    ...previous,
    newGroupsAndParticipants: groupsAndParticipants,
  };
};

export const licenseInformationWithRemovedGroup = (state: RemoveGroup): GetLicenseInformation => {
  const { kind, newGroupsAndParticipants, ...previous } = state;
  const groupsAndParticipants = newGroupsAndParticipants.filter(
    (participantOrGroup) =>
      (participantOrGroup.payload.id.toString() !== state.groupId &&
        'participants' in participantOrGroup.payload) ||
      'email' in participantOrGroup.payload,
  );

  return {
    kind: 'get-license-information',
    ...previous,
    newGroupsAndParticipants: groupsAndParticipants,
  };
};

export const setTimezone = (state: Inviting, timeZone: string): Inviting => {
  return {
    ...state,
    timeZone,
  };
};

export const setSkipCommunications = (state: Inviting, skipCommunications: boolean): Inviting => {
  return {
    ...state,
    skipCommunications,
  };
};

export const error = (state: Submitting, message: TPlainTextKey): InvitingError => ({
  ...state,
  kind: 'error',
  message,
});

export type State =
  | Ready
  | InvitingError
  | NewParticipant
  | NewGroup
  | RemoveParticipant
  | RemoveGroup
  | Inviting
  | Submitting
  | ParticipantsInvited
  | GetLicenseInformation;
